import {
  Box,
  Text,
  VStack,
  HStack,
  Icon,
  Badge,
  Stack,
} from "@chakra-ui/react";
import { Controller, Control } from "react-hook-form";
import { FaUserFriends } from "react-icons/fa";
import { formatDate } from "src/utils";
import dayjs from "dayjs";
import { KYDCheckbox } from "src/ui";
import { AudienceData } from "@common/interfaces/KYDMarketing";

/** Stable id for targeting: Meta rows use `id`; form values often only have `external_id`. */
export function audienceTargetingKey(a: AudienceData): string {
  const v = a.external_id ?? a.id;
  if (v != null && String(v).trim() !== "") {
    return String(v).trim();
  }
  // CSV rows created in the flow may only have `file_key` until Meta assigns an audience id.
  if (a.audience_type === "csv" && "file_key" in a && a.file_key) {
    const fk = String(a.file_key).trim();
    if (fk) return `csv:${fk}`;
  }
  // Page-based audiences (FB/IG) before Meta assigns custom audience ids
  if (a.audience_type === "page_engagers" && "page_id" in a && a.page_id) {
    const pid = String(a.page_id).trim();
    if (pid) {
      const pt = "page_type" in a && a.page_type ? a.page_type : "";
      return `page_engagers:${pt}:${pid}`;
    }
  }
  if (a.audience_type === "page_lookalike" && "page_id" in a && a.page_id) {
    const pid = String(a.page_id).trim();
    if (pid) {
      const pt = "page_type" in a && a.page_type ? a.page_type : "";
      const lk = "lookalike" in a ? a.lookalike : undefined;
      const ratio = lk?.ratio != null ? String(lk.ratio) : "";
      const lname = (lk?.name ?? "").trim();
      return `page_lookalike:${pt}:${pid}:${ratio}:${lname}`;
    }
  }
  // KYD-built audiences may exist only as draft rows until sync (legacy rows without `id`)
  if (a.audience_type === "kyd") {
    const nm = (a.name ?? "").trim();
    const created =
      "created_at" in a && a.created_at ? String(a.created_at).trim() : "";
    if (nm && created) return `kyd:${nm}:${created}`;
    if (nm) return `kyd:${nm}`;
  }
  return "";
}

export function isCustomAudienceRowSelected(
  selected: AudienceData[],
  row: AudienceData
): boolean {
  const key = audienceTargetingKey(row);
  if (!key) return false;
  return selected.some((s) => audienceTargetingKey(s) === key);
}

const handleAudienceSelection = (
  audience: AudienceData,
  currentValue: AudienceData[],
  onChange: (value: AudienceData[]) => void,
  isChecked: boolean
) => {
  if (isChecked) {
    const key = audienceTargetingKey(audience);
    if (!key) {
      onChange(currentValue);
      return;
    }
    if (currentValue.some((s) => audienceTargetingKey(s) === key)) {
      onChange(currentValue);
      return;
    }
    const customCreatedTypes = new Set([
      "csv",
      "kyd",
      "page_engagers",
      "page_lookalike",
    ]);
    if (
      audience.audience_type &&
      customCreatedTypes.has(audience.audience_type)
    ) {
      onChange([...currentValue, { ...audience }]);
      return;
    }
    onChange([
      ...currentValue,
      {
        ...audience,
        id: undefined,
        description: audience.description,
        audience_type: "meta",
        subtype: "subtype" in audience ? audience.subtype : "",
        external_id: key,
      },
    ]);
  } else {
    const rowKey = audienceTargetingKey(audience);
    if (!rowKey) {
      onChange(currentValue);
      return;
    }
    onChange(
      currentValue.filter(
        (selected: AudienceData) => audienceTargetingKey(selected) !== rowKey
      )
    );
  }
};

interface AudienceListProps {
  audiences: AudienceData[];
  control: Control<any>;
  fieldName: string;
}

const AudienceList = ({ audiences, control, fieldName }: AudienceListProps) => {
  return (
    <Stack w="full" gap={1} alignItems="flex-start">
      {audiences.map((audience) => (
        <Stack
          key={audience.external_id || audience.id}
          w="full"
          alignItems="flex-start"
          gap={0}
          borderRadius="md"
          border="1px solid"
          overflow="hidden"
          justifyItems="flex-start"
          borderColor="gray.200"
          p={2}
        >
          <HStack justifyContent="flex-start" w="full">
            <HStack flexGrow={1} alignItems="center" gap={4}>
              <Box
                bg="gray.100"
                p={2}
                borderRadius="md"
                w={10}
                height={10}
                alignItems="center"
                justifyContent="center"
                display={"flex"}
              >
                <Icon as={FaUserFriends} />
              </Box>
              <VStack gap={0} flexGrow={1} alignItems="flex-start" w="full">
                <HStack w="full" alignItems="center">
                  <Text
                    noOfLines={1}
                    wordBreak="break-word"
                    fontWeight={"medium"}
                    flexWrap={"wrap"}
                    fontSize="sm"
                  >
                    {audience.name}
                  </Text>
                </HStack>
                {audience.audience_type === "meta" && (
                  <>
                    <Text fontSize="sm" color="gray.600" noOfLines={1}>
                      Created at {formatDate(dayjs(audience.time_created))}
                    </Text>
                  </>
                )}
              </VStack>
            </HStack>
            <HStack
              gap={2}
              justifyContent="flex-end"
              flexShrink={[1, 0]}
              flexWrap="wrap"
              // display={["none", "flex"]}
            >
              {!audience.id && <Badge colorScheme="green">New</Badge>}
              <Badge>{audience.audience_type}</Badge>
              {"subtype" in audience && <Badge>{audience.subtype}</Badge>}
              {"approximate_count_upper_bound" in audience && (
                <Box
                  fontSize="xs"
                  rounded="md"
                  textAlign={"center"}
                  px={1}
                  textTransform="lowercase"
                  borderColor="gray.300 !important"
                  border="1px solid"
                  flexShrink={0}
                  fontWeight={"medium"}
                >
                  ~{audience.approximate_count_upper_bound} people
                </Box>
              )}
            </HStack>
            <Controller
              control={control}
              name={fieldName}
              render={({ field }) => (
                <KYDCheckbox
                  rounded="sm"
                  isChecked={isCustomAudienceRowSelected(
                    field.value || [],
                    audience
                  )}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    const currentValue = field.value || [];
                    handleAudienceSelection(
                      audience,
                      currentValue,
                      field.onChange,
                      isChecked
                    );
                  }}
                />
              )}
            />
          </HStack>
        </Stack>
      ))}
    </Stack>
  );
};

export default AudienceList;
